import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, Truck, Sparkles, Stethoscope, Search } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site";
import GlobalSearch from "@/components/GlobalSearch";

const SERVICE_SUBMENU = [
    { label: "Внос по поръчка", href: "/services/import", icon: Truck, desc: "Заявка за внос на желан автомобил" },
    { label: "Детайлинг", href: "/services/detailing", icon: Sparkles, desc: "Записване на час за детайлинг" },
    { label: "Диагностика", href: "/services/diagnostics", icon: Stethoscope, desc: "Записване за диагностика" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setSearchOpen(false);
    }, [location.pathname]);

    // Cmd/Ctrl+K opens search
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-bg/70 backdrop-blur-xl border-b border-line"
                    : "bg-transparent"
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="flex items-center justify-between h-20 gap-6">
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="site-logo">
                        <img
                            src="/ks-logo.png"
                            alt="Konstantin KS Monogram"
                            className="w-10 h-12 object-contain"
                        />
                        <span className="font-display text-xl xl:text-2xl tracking-tight whitespace-nowrap hidden sm:inline">
                            <span className="text-gradient-gold">Konstantin</span>
                            <span className="text-ink/80">.mobile.bg</span>
                        </span>
                    </Link>

                    <nav className="hidden xl:flex items-center gap-6 2xl:gap-8">
                        {NAV_LINKS.map((l) => {
                            if (l.section === "services") {
                                return (
                                    <div key={l.label} className="relative group" data-testid="nav-services-dropdown">
                                        <button
                                            type="button"
                                            data-testid={`nav-${l.section}`}
                                            className="relative inline-flex items-center gap-1 text-[13px] uppercase tracking-[0.2em] text-ink/70 hover:text-gold transition-colors duration-300 whitespace-nowrap cursor-pointer"
                                        >
                                            {l.label}
                                            <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                                        </button>
                                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                                            <div className="w-[340px] bg-surface border border-line rounded-2xl shadow-soft overflow-hidden">
                                                {SERVICE_SUBMENU.map(({ label, href, icon: Icon, desc }) => (
                                                    <a
                                                        key={label}
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`nav-svc-${href.split("/").pop()}`}
                                                        className="flex items-start gap-4 px-5 py-4 hover:bg-bg transition-colors group/item"
                                                    >
                                                        <span className="w-10 h-10 rounded-lg bg-gold/10 text-gold border border-gold/20 flex items-center justify-center shrink-0 group-hover/item:bg-gold/15">
                                                            <Icon size={18} />
                                                        </span>
                                                        <span>
                                                            <span className="block text-sm font-medium text-ink">{label}</span>
                                                            <span className="block text-xs text-ink-muted mt-0.5">{desc}</span>
                                                        </span>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <a
                                    key={l.label}
                                    href={l.href}
                                    data-testid={`nav-${l.section}`}
                                    className="relative text-[13px] uppercase tracking-[0.2em] text-ink/70 hover:text-gold transition-colors duration-300 group whitespace-nowrap"
                                >
                                    {l.label}
                                    <span className="absolute -bottom-2 left-0 right-0 h-px bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                                </a>
                            );
                        })}
                    </nav>

                    <div className="hidden xl:flex items-center gap-4 shrink-0">
                        <button
                            type="button"
                            onClick={() => setSearchOpen(true)}
                            data-testid="navbar-search-btn"
                            aria-label="Търсене в сайта"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-line hover:border-gold/40 text-ink-muted hover:text-gold transition-colors"
                        >
                            <Search size={14} />
                            <span className="text-xs tracking-wide hidden 2xl:inline">Търси</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg border border-line text-ink-subtle hidden 2xl:inline">⌘K</span>
                        </button>
                        <a
                            href={`tel:${SITE.phoneRaw}`}
                            className="flex items-center gap-2 text-ink/80 hover:text-gold transition-colors whitespace-nowrap"
                            data-testid="navbar-phone"
                        >
                            <Phone size={16} />
                            <span className="text-sm tracking-wide">{SITE.phone}</span>
                        </a>
                    </div>

                    <button
                        type="button"
                        onClick={() => setSearchOpen(true)}
                        className="xl:hidden p-2 text-ink"
                        data-testid="navbar-search-btn-mobile"
                        aria-label="Търсене"
                    >
                        <Search size={22} />
                    </button>

                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="xl:hidden p-2 text-ink"
                        data-testid="navbar-menu-toggle"
                        aria-label="Меню"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="xl:hidden bg-bg/95 backdrop-blur-xl border-t border-line"
                    >
                        <nav className="px-6 py-8 flex flex-col gap-6">
                            {NAV_LINKS.map((l) => (
                                <div key={l.label}>
                                    {l.section === "services" ? (
                                        <span
                                            className="text-base tracking-[0.2em] uppercase text-ink/80"
                                            data-testid={`mobile-nav-${l.section}`}
                                        >
                                            {l.label}
                                        </span>
                                    ) : (
                                        <a
                                            href={l.href}
                                            className="text-base tracking-[0.2em] uppercase text-ink/80 hover:text-gold"
                                            data-testid={`mobile-nav-${l.section}`}
                                        >
                                            {l.label}
                                        </a>
                                    )}
                                    {l.section === "services" && (
                                        <div className="mt-3 ml-3 flex flex-col gap-3 border-l-2 border-gold/30 pl-4">
                                            {SERVICE_SUBMENU.map(({ label, href, icon: Icon }) => (
                                                <a
                                                    key={label}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`mobile-svc-${href.split("/").pop()}`}
                                                    className="inline-flex items-center gap-2 text-sm text-ink/80 hover:text-gold"
                                                >
                                                    <Icon size={14} className="text-gold" />
                                                    {label}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            <a
                                href={`tel:${SITE.phoneRaw}`}
                                className="flex items-center gap-2 text-gold pt-4 border-t border-line"
                            >
                                <Phone size={16} /> {SITE.phone}
                            </a>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
            <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
        </motion.header>
    );
}
